import { useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { fetchWithAuth, isAuthenticated } from "../utils/fetchWithAuth";

/**
 * Records today's login for the consecutive-day streak (idempotent per calendar
 * day) and, only on the FIRST open of the day, surfaces the streak as a toast.
 * No persistent UI — the toast is the whole feature.
 *
 * Safe to mount in more than one place: the per-day idempotency means later
 * pings return advanced=false and stay silent.
 */
export function useStreakPing() {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    // A streak is a property of an account, so with no token this can only
    // 401. It still fired on the anonymous get-started flow, because the
    // "inert" dashboard preview on the reveal screen mounts the real
    // AppNavbar, which mounts this hook. That put a guaranteed-failing POST on
    // the slowest connection we serve, at the exact moment we're asking
    // someone to sign up.
    if (!isAuthenticated()) return;
    done.current = true;
    fetchWithAuth("/api/accounts/me/streak/", { method: "POST", json: {} })
      .then((d) => {
        if (!d?.advanced || !d.streak) return; // already counted today, or no streak
        const days = d.days_to_next_point;
        const tail = d.earned_point
          ? " - +1 Sift Score!"
          : days != null
            ? ` · ${days} day${days === 1 ? "" : "s"} to your next point`
            : "";
        toast(`${d.streak}-day login streak${tail}`, { icon: "🔥", duration: 5000 });
      })
      .catch(() => {});
  }, []);
}
